/**
 * AnnotationThread — inline annotation expansion below a change (M4 Phase 4.4).
 *
 * Per the M4 design decision, annotations are NOT shown in a popover —
 * they expand inline beneath the change block so reviewers don't lose
 * the context they were reading. The component renders:
 *
 *   [count badge / toggle row]
 *      ├─ existing annotations (author + relative time + body)
 *      │     └─ author-only: edit / delete entries while edit window open
 *      └─ "add annotation" textarea (only when canAuthor === true)
 *
 * State management is delegated to the `useAnnotations` hook upstream; this
 * component only owns local UI state (open/closed, draft body, edit form).
 *
 * Keyboard:
 *   - Cmd/Ctrl+Enter inside the textarea submits.
 *   - Escape collapses the thread without losing the draft.
 */
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';

import type {
  AnnotationResponse,
  AnnotationVisibility,
  VeriDocRole,
} from '../../lib/veridoc-api';

import { formatRelativeTime } from './DecidableChange';
import type { UseAnnotationsResult } from './useAnnotations';

const ROLE_LABELS: Record<VeriDocRole, string> = {
  lawyer: '律师',
  reviewer: '审核员',
  viewer: '查阅者',
};

// ADR-120 D7 — visibility labels shown in the segmented control + the
// author's own lock indicator aria-label. Shared has no indicator
// (already public) so its label here is only used in the selector.
const VISIBILITY_LABELS: Record<AnnotationVisibility, string> = {
  private: '仅自己',
  team: '团队',
  shared: '所有人',
};

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: AnnotationVisibility;
  label: string;
  hint: string;
}> = [
  { value: 'private', label: '仅自己', hint: '仅作者本人可见' },
  { value: 'team', label: '团队', hint: '律师/审核员可见' },
  { value: 'shared', label: '所有人', hint: '所有可见该比对的角色（含查阅者）可见' },
];

interface AnnotationThreadProps {
  changeId: string;
  annotations: UseAnnotationsResult;
}

export const AnnotationThread = memo(function AnnotationThread({
  changeId,
  annotations,
}: AnnotationThreadProps): React.JSX.Element {
  const items = annotations.list(changeId);
  const pending = annotations.isPending(changeId);
  const canAuthor = annotations.canAuthor;
  const currentUserId = annotations.currentUserId;

  // Auto-open the thread when annotations exist; otherwise keep collapsed
  // so the diff stays visually compact for reviewers who only care about
  // accept / reject. The user can still toggle either way.
  const [open, setOpen] = useState<boolean>(items.length > 0);
  const [draft, setDraft] = useState<string>('');
  // ADR-120 — visibility defaults to 'private' (least-exposure baseline).
  // State intentionally persists across successive creates so an author
  // writing several notes in a row keeps the same audience without
  // re-selecting each time.
  const [visibility, setVisibility] = useState<AnnotationVisibility>('private');

  useEffect(() => {
    // If a new annotation arrives (e.g. another role added one), surface it
    // by opening the thread. We don't auto-collapse on emptying so the user
    // doesn't lose their draft.
    if (items.length > 0 && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const trimmed = draft.trim();
      if (!trimmed) return;
      const created = await annotations.create(changeId, trimmed, visibility);
      if (created) setDraft('');
    },
    [annotations, changeId, draft, visibility],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        void handleSubmit(event as unknown as FormEvent);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    },
    [handleSubmit],
  );

  return (
    <div
      data-testid="annotation-thread"
      data-change-id={changeId}
      className="mt-2 ml-1 border-l-2 border-ink-100 pl-3"
      role="region"
      aria-label={items.length > 0 ? `${items.length} 条标注` : '标注'}
      // Stop click bubbling so opening/closing doesn't trigger
      // DecidableChange's navigation.select.
      onClick={(e) => e.stopPropagation()}
    >
      <ToggleRow
        count={items.length}
        open={open}
        canAuthor={canAuthor}
        onToggle={() => setOpen((v) => !v)}
      />
      {open && (
        <div className="mt-1 space-y-2" role="list">
          {items.map((annotation) => (
            <AnnotationItem
              key={annotation.id}
              annotation={annotation}
              currentUserId={currentUserId}
              pendingChange={pending}
              onUpdate={(body) => annotations.update(annotation.id, body)}
              onDelete={() => annotations.remove(annotation.id)}
            />
          ))}
          {canAuthor && (
            <form onSubmit={handleSubmit} className="mt-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="添加标注…(Cmd/Ctrl+Enter 提交)"
                rows={2}
                maxLength={5000}
                disabled={pending}
                className="block w-full rounded-md border border-ink-200 bg-ink-0 px-2 py-1.5 text-xs text-ink-900 placeholder-ink-400 focus:border-brand-500 focus:outline-none disabled:opacity-60"
              />
              <div className="mt-1 flex items-center justify-between gap-2 text-xs text-ink-400">
                <VisibilitySelector
                  value={visibility}
                  onChange={setVisibility}
                  disabled={pending}
                />
                <div className="flex items-center gap-2">
                  <span>{draft.length}/5000</span>
                  <button
                    type="submit"
                    disabled={pending || !draft.trim()}
                    className={[
                      'inline-flex h-6 items-center rounded-sm px-2 text-xs font-medium text-white transition-colors duration-fast',
                      pending || !draft.trim()
                        ? 'cursor-not-allowed bg-ink-300'
                        : 'bg-brand-600 hover:bg-brand-700',
                    ].join(' ')}
                  >
                    {pending ? '提交中…' : '提交'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
});

function ToggleRow({
  count,
  open,
  canAuthor,
  onToggle,
}: {
  count: number;
  open: boolean;
  canAuthor: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  let label: string;
  if (count === 0) {
    label = canAuthor ? '+ 添加标注' : '暂无标注';
  } else {
    label = `${count} 条标注${open ? ' (折叠)' : ''}`;
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!canAuthor && count === 0}
      data-testid="annotation-toggle"
      className="text-[11px] font-medium text-brand-700 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-ink-300 disabled:no-underline"
    >
      {label}
    </button>
  );
}

/**
 * VisibilitySelector — ADR-120 D7 segmented control for annotation audience.
 *
 * Three options (private / team / shared) rendered as a radiogroup so screen
 * readers announce the current selection and keyboard users can arrow-key
 * between options. Default is 'private' (least-exposure baseline).
 */
function VisibilitySelector({
  value,
  onChange,
  disabled,
}: {
  value: AnnotationVisibility;
  onChange: (next: AnnotationVisibility) => void;
  disabled: boolean;
}): React.JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label="可见性"
      data-testid="visibility-selector"
      className="inline-flex items-center gap-0.5 rounded-sm border border-ink-200 bg-ink-0 p-0.5"
    >
      {VISIBILITY_OPTIONS.map((option) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked ? 'true' : 'false'}
            aria-label={option.label}
            title={option.hint}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            data-testid={`visibility-option-${option.value}`}
            className={[
              'inline-flex h-5 items-center rounded-sm px-1.5 text-[10px] font-medium transition-colors duration-fast',
              checked
                ? 'bg-brand-600 text-white'
                : 'text-ink-500 hover:bg-ink-100',
              disabled ? 'cursor-not-allowed opacity-60' : '',
            ].join(' ')}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * VisibilityLockIcon — ADR-120 D7 small lock shown only on the author's
 * own non-shared annotations. The affordance reminds the author that
 * "this is mine and not everyone can see it" without exposing the
 * visibility dimension to other readers (who only ever see rows that
 * already passed the server-side filter).
 */
function VisibilityLockIcon({
  visibility,
}: {
  visibility: AnnotationVisibility;
}): React.JSX.Element {
  const label = `可见性：${VISIBILITY_LABELS[visibility]}`;
  return (
    <span
      data-testid="annotation-visibility-indicator"
      aria-label={label}
      title={label}
      className="ml-1 inline-flex text-ink-400"
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 14 14"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M7 1.5a2.5 2.5 0 0 0-2.5 2.5v1H4a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1h-.5V4A2.5 2.5 0 0 0 7 1.5zm-1.5 2.5a1.5 1.5 0 0 1 3 0v1h-3V4z" />
      </svg>
    </span>
  );
}

interface AnnotationItemProps {
  annotation: AnnotationResponse;
  currentUserId: string | undefined;
  pendingChange: boolean;
  onUpdate: (body: string) => Promise<AnnotationResponse | null>;
  onDelete: () => Promise<boolean>;
}

/**
 * useEditCountdown — drives the per-second re-render needed to visualise
 * the annotation 5-minute edit window (P2-3).
 *
 * Accepts the ISO expiry timestamp or `null` (when the annotation is not
 * editable by the viewer). Returns `remainingMs` (clamped at 0) and
 * `isExpired` so the consumer can both render the countdown and gate the
 * edit / delete buttons off the same source of truth.
 *
 * The interval is only armed while the window is still open, and is torn
 * down on unmount / once expiry is reached, so inactive threads cost zero
 * timers.
 */
function useEditCountdown(expiresAtIso: string | null): {
  remainingMs: number;
  isExpired: boolean;
} {
  const expiresAtMs = expiresAtIso !== null ? Date.parse(expiresAtIso) : NaN;
  const compute = useCallback((): { remainingMs: number; isExpired: boolean } => {
    if (!Number.isFinite(expiresAtMs)) {
      return { remainingMs: 0, isExpired: true };
    }
    const remaining = expiresAtMs - Date.now();
    if (remaining <= 0) return { remainingMs: 0, isExpired: true };
    return { remainingMs: remaining, isExpired: false };
  }, [expiresAtMs]);

  const [snapshot, setSnapshot] = useState(compute);

  useEffect(() => {
    setSnapshot(compute());
    if (!Number.isFinite(expiresAtMs)) return;
    // Re-arm only while the window is open. After expiry we stop ticking —
    // `canEdit` reverts to false and the countdown unmounts.
    if (expiresAtMs <= Date.now()) return;
    const id = window.setInterval(() => {
      const next = compute();
      setSnapshot(next);
      if (next.isExpired && id !== null) window.clearInterval(id);
    }, 1000);
    return () => {
      if (id !== null) window.clearInterval(id);
    };
  }, [compute, expiresAtMs]);

  return snapshot;
}

function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(minutes >= 10 ? 2 : 1, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${mm}:${ss}`;
}

const COUNTDOWN_WARNING_THRESHOLD_MS = 60 * 1000;

function CountdownPill({ remainingMs }: { remainingMs: number }): React.JSX.Element {
  const isWarning = remainingMs <= COUNTDOWN_WARNING_THRESHOLD_MS;
  return (
    <span
      data-testid="edit-countdown"
      data-warning={isWarning ? 'true' : 'false'}
      className={
        isWarning
          ? 'font-medium text-semantic-warning'
          : 'text-ink-400'
      }
      title="编辑窗口剩余时间"
    >
      {formatCountdown(remainingMs)} 后不可编辑
    </span>
  );
}

const AnnotationItem = memo(function AnnotationItem({
  annotation,
  currentUserId,
  pendingChange,
  onUpdate,
  onDelete,
}: AnnotationItemProps): React.JSX.Element {
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(annotation.body);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const isOwnAnnotation = annotation.authorId === currentUserId;
  const { remainingMs, isExpired } = useEditCountdown(
    isOwnAnnotation ? annotation.editWindowExpiresAt : null,
  );
  const canEdit = isOwnAnnotation && !isExpired;

  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  // Sync draft when the annotation body changes externally (e.g. cross-tab).
  useEffect(() => {
    if (!editing) setDraft(annotation.body);
  }, [annotation.body, editing]);

  const handleSave = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === annotation.body) {
      setEditing(false);
      return;
    }
    const updated = await onUpdate(trimmed);
    if (updated) setEditing(false);
  }, [annotation.body, draft, onUpdate]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        void handleSave();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setEditing(false);
        setDraft(annotation.body);
      }
    },
    [annotation.body, handleSave],
  );

  return (
    <article
      role="listitem"
      data-testid="annotation-item"
      data-annotation-id={annotation.id}
      className="rounded-md border border-ink-100 bg-ink-50 px-2 py-1.5 text-xs text-ink-700"
    >
      <header className="flex items-center justify-between gap-2 text-[11px] text-ink-500">
        <span className="font-medium text-ink-900">
          {annotation.authorDisplayName}
          <span className="ml-1 rounded-sm bg-ink-100 px-1 text-[10px] text-ink-500">
            {ROLE_LABELS[annotation.authorRole] ?? annotation.authorRole}
          </span>
          {isOwnAnnotation && annotation.visibility !== 'shared' && (
            <VisibilityLockIcon visibility={annotation.visibility} />
          )}
        </span>
        <span>
          {formatRelativeTime(annotation.createdAt)}
          {annotation.updatedAt !== annotation.createdAt && (
            <span className="ml-1 text-ink-400">(已编辑)</span>
          )}
        </span>
      </header>
      {editing ? (
        <div className="mt-1.5">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            maxLength={5000}
            disabled={pendingChange}
            className="block w-full rounded-sm border border-ink-200 bg-ink-0 px-2 py-1 text-xs text-ink-900 focus:border-brand-500 focus:outline-none disabled:opacity-60"
          />
          <div className="mt-1 flex items-center justify-end gap-2 text-[11px]">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setDraft(annotation.body);
              }}
              disabled={pendingChange}
              className="text-ink-500 hover:text-ink-700"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={pendingChange || !draft.trim()}
              className={[
                'inline-flex h-5 items-center rounded-sm px-2 text-[11px] font-medium text-white',
                pendingChange || !draft.trim()
                  ? 'cursor-not-allowed bg-ink-300'
                  : 'bg-brand-600 hover:bg-brand-700',
              ].join(' ')}
            >
              保存
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-ink-800">
          {annotation.body}
        </p>
      )}
      {!editing && canEdit && (
        <div className="mt-1 flex items-center justify-end gap-2 text-[11px] text-ink-400">
          <CountdownPill remainingMs={remainingMs} />
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={pendingChange}
            className="hover:text-ink-700"
          >
            编辑
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('确定要删除这条标注吗？')) {
                void onDelete();
              }
            }}
            disabled={pendingChange}
            className="hover:text-semantic-danger"
          >
            删除
          </button>
        </div>
      )}
    </article>
  );
});

export default AnnotationThread;
